import {useState, useEffect, useRef} from 'react'
import { AlertTriangle, Tv, Monitor } from 'lucide-react'
import * as Body from './Styles'
import * as Functions from '../../utils/functions/functions'
import Loading from '../../components/Loading/Loading'
import Spinner from '../../assets/icons/spinner.svg'
import SensorBadge from '../../components/SensorBadge/SensorBadge'
import SimpleTernoChart from './SimpleTernoChart'

import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux'
import api from '../../utils/axiosInstance'
import * as Types from '../../utils/redux/types'
import { ToggleSwitch } from '../../components/ToggleSwitch/ToggleSwitch'
import { AutoProgressBar } from '../../components/AutoProgressBar/AutoProgressBar'
import { getApiBaseUrl } from '../../utils/defines/apiUrl'
import { isSensorInvalido, getSensorLabel, THRESHOLDS_MANUTENCAO as TH } from '../../utils/alarmConfig'
import { USE_MOCK } from '../../utils/mock/mockFlag'
import { mockDashboard } from '../../utils/mock/mockData'
import { useIsMobile } from '../../utils/hooks/useIsMobile'
import Modal from '../../components/Modal/Modal'
import Button from '../../components/Button/Button'

const AUTO_SWITCH_ON_KEY = 'dashboard-auto-switch-on'
const AUTO_SWITCH_SECONDS_KEY = 'dashboard-auto-switch-seconds'
const AUTO_SWITCH_MIN_SECONDS = 5
const AUTO_SWITCH_DEFAULT_SECONDS = 60
const TV_MODE_KEY = 'dashboard-tv-mode'

export type AxiosMoendaResponse = {
  moendas: MoendaToken[];
  moendaAtual: MoendaAtual;
  grafico: GraficoPorTerno[];
};

export type MoendaToken = {
  token: string;
  nome: string;
};

export type MoendaAtual = {
  nome: string;
  ultimo_envio: number;
  offline: boolean;
  ternos: Terno[];
  unidadeHidraulica: Types.UnidadeHidraulicaType;
};

export type Terno = {
  terno_id: number;
  nome_terno: string;
  manual: boolean;
  funcionando: boolean;
};

export type GraficoPorTerno = {
  terno_id: number;
  data: RegistroGrafico[];
};

export type RegistroGrafico = {
  _id: null;
  timestamp: string;
  pressao_le: number;
  pressao_ld: number;
  deslocamento_le: number;
  deslocamento_ld: number;
};

const SituacaoBomba = {
    OK: 0,
    PROBLEMA_BOMBA_1: 1,
    PROBLEMA_BOMBA_2: 2,
    PROBLEMA_BOMBA_1_E_2: 3,
    PROBLEMA_BOMBA_3: 4,
    PROBLEMA_TRES_BOMBAS: 5,
    PROBLEMA_BOMBA_1_E_3: 6,
    PROBLEMA_BOMBA_2_E_3: 7
}

const Dashboard = () => {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const isMobile = useIsMobile()

    useEffect(() => {
        if (isMobile) navigate('/inicio', { replace: true })
    }, [isMobile, navigate])

    const [currentUsina, setCurrentUsina] = useState<AxiosMoendaResponse | null>(null)
    const [currentUsinaIndex, setCurrentUsinaIndex] = useState(0)

    const [isModalOpen, setModalState] = useState(false)
    const [isLoading, setLoading] = useState(true)
    // Nome da usina sendo carregada numa troca (manual ou automática) — só usado quando já há
    // uma usina em tela, para mostrar um indicador pequeno em vez de bloquear a tela toda.
    const [trocandoPara, setTrocandoPara] = useState<string | null>(null)

    const [autoSwitchOn, setAutoSwitchOn] = useState<boolean>(() => localStorage.getItem(AUTO_SWITCH_ON_KEY) === 'true')
    const [autoSwitchSeconds, setAutoSwitchSeconds] = useState<number>(() => {
        const saved = Number(localStorage.getItem(AUTO_SWITCH_SECONDS_KEY))
        return saved >= AUTO_SWITCH_MIN_SECONDS ? saved : AUTO_SWITCH_DEFAULT_SECONDS
    })
    const [tvMode, setTvMode] = useState<boolean>(() => localStorage.getItem(TV_MODE_KEY) === 'true')

    useEffect(() => {
        localStorage.setItem(TV_MODE_KEY, String(tvMode))
    }, [tvMode])

    const fetchURL = useRef(`${getApiBaseUrl()}/dashboard`)

    // Em modo mock, deriva a usina atual a partir do token na própria fetchURL,
    // assim a troca manual/automática reflete qual usina está "selecionada".
    const buildMockResponse = (): AxiosMoendaResponse => {
        const urlToken = new URL(fetchURL.current, window.location.origin).searchParams.get('token')
        const usina = mockDashboard.moendas.find(m => m.token === urlToken) ?? mockDashboard.moendas[0]
        return { ...mockDashboard, moendaAtual: { ...mockDashboard.moendaAtual, nome: usina.nome } } as unknown as AxiosMoendaResponse
    }

    const handleComplete = async () => {
        if (USE_MOCK) {
            setCurrentUsina(buildMockResponse())
            setLoading(false)
            setTrocandoPara(null)
            return
        }
        try {
            const response = await api.get(fetchURL.current)
            setCurrentUsina(response.data)
            setLoading(false)
            setTrocandoPara(null)
        } catch (error) {
            console.error('Erro na requisição:', error);
            setLoading(false)
            setTrocandoPara(null)
        }
    };

    useEffect(() => {
        if (USE_MOCK) {
            setCurrentUsina(buildMockResponse())
            setLoading(false)
            return
        }
        api.get(fetchURL.current)
            .then(response => {
                setCurrentUsina(response.data)
                setLoading(false)
            })
            .catch((error) => {
                console.error('Erro na requisição:', error);
                Functions.ResolveErrors(error.response.status, error.response.data.code, error.response.data.message, navigate, dispatch, error.response.data.content)
            })
    }, [])

    useEffect(() => {
        localStorage.setItem(AUTO_SWITCH_ON_KEY, String(autoSwitchOn))
    }, [autoSwitchOn])

    useEffect(() => {
        localStorage.setItem(AUTO_SWITCH_SECONDS_KEY, String(autoSwitchSeconds))
    }, [autoSwitchSeconds])

    function geUTCStringFromTimestamp(timestamp: number): string {

        const valor = typeof timestamp === 'string' ? Number(timestamp) : timestamp;
        const date = new Date(valor);

        if (isNaN(date.getTime())) {
            return 'Data inválida';
        }

        const dd = String(date.getUTCDate()).padStart(2, '0');
        const mm = String(date.getUTCMonth() + 1).padStart(2, '0'); // mês começa do zero
        const yyyy = date.getUTCFullYear();

        const hh = String(date.getUTCHours()).padStart(2, '0');
        const min = String(date.getUTCMinutes()).padStart(2, '0');
        const ss = String(date.getUTCSeconds()).padStart(2, '0');

        return `${dd}/${mm}/${yyyy} às ${hh}:${min}:${ss} UTC`;
    }

    function gerarStatusInativos(ternos: Terno[]): string {
        const naoMoendo = ternos
            .filter(t => !t.funcionando)
            .map(t => t.nome_terno);

        const foraDoAutomatico = ternos
            .filter(t => t.manual)
            .map(t => t.nome_terno);

        const textoNaoMoendo = naoMoendo.length
            ? `Ternos que NÃO estão moendo: ${naoMoendo.join(', ')}.`
            : 'Todos os ternos estão moendo.';

        const textoForaDoAutomatico = foraDoAutomatico.length
            ? `Ternos que NÃO estão em modo automático: ${foraDoAutomatico.join(', ')}.`
            : 'Todos os ternos estão em modo automático.';

        return `${textoNaoMoendo} ${textoForaDoAutomatico}`;
    }

    const getAlertsOil = (UH: Types.UnidadeHidraulicaType) => {

        if(currentUsina?.moendaAtual.offline)
            return <Body.AlertText>● Alertas de operação não são gerados com a usina offline.</Body.AlertText>

        const vet: string[] = []

        if (isSensorInvalido(UH.oleo.temperatura)) {
            vet.push(`Temperatura do óleo: ${getSensorLabel(UH.oleo.temperatura)}.`)
        } else if (UH.oleo.temperatura >= TH.temperatura.critico) {
            vet.push('Temperatura acima da máxima. Bombas bloqueadas para impedir danos ao sistema.')
        } else if (UH.oleo.temperatura >= TH.temperatura.atencao) {
            vet.push('Temperatura próxima da máxima. Verificar funcionamento do trocador de óleo.')
        }

        const tempOk = !isSensorInvalido(UH.oleo.temperatura) && UH.oleo.temperatura > 25

        if (isSensorInvalido(UH.oleo.trans_fil_suj_1)) {
            vet.push(`Filtro 1: ${getSensorLabel(UH.oleo.trans_fil_suj_1)}.`)
        } else if (tempOk && UH.oleo.trans_fil_suj_1 >= TH.filtro.critico) {
            vet.push('Nível de saturação do filtro 1 em nível crítico.')
        } else if (tempOk && UH.oleo.trans_fil_suj_1 >= TH.filtro.atencao) {
            vet.push('Nível de saturação do filtro 1 próximo ao limite. Programar troca.')
        }

        if (UH.oleo.trans_fil_suj_2 !== null) {
            if (isSensorInvalido(UH.oleo.trans_fil_suj_2)) {
                vet.push(`Filtro 2: ${getSensorLabel(UH.oleo.trans_fil_suj_2)}.`)
            } else if (tempOk && UH.oleo.trans_fil_suj_2 >= TH.filtro.critico) {
                vet.push('Nível de saturação do filtro 2 em nível crítico.')
            } else if (tempOk && UH.oleo.trans_fil_suj_2 >= TH.filtro.atencao) {
                vet.push('Nível de saturação do filtro 2 próximo ao limite. Programar troca.')
            }
        }

        if (isSensorInvalido(UH.oleo.trans_umidade)) {
            vet.push(`Umidade do óleo: ${getSensorLabel(UH.oleo.trans_umidade)}.`)
        } else if (UH.oleo.trans_umidade > 200) {
            vet.push('Falha na leitura do transmissor de umidade.')
        } else if (UH.oleo.trans_umidade > TH.umidade.critico) {
            vet.push('Umidade em nível crítico. Providenciar imediatamente a remoção do excesso de umidade.')
        } else if (UH.oleo.trans_umidade > TH.umidade.atencao) {
            vet.push('Nível de umidade em estado de atenção. Providenciar remoção de excesso de umidade.')
        }

        if (UH.oleo.alarme_conjunto_filtro_1 === 2) vet.push('Possível problema no conjunto do filtro 1.')
        if (UH.oleo.alarme_conjunto_filtro_2 === 2) vet.push('Possível problema no conjunto do filtro 2.')

        vet.push(gerarStatusInativos(currentUsina!.moendaAtual.ternos))

        if(vet.length == 0)
            return <Body.AlertText>● Nenhum alerta imediato de operação </Body.AlertText>

        return vet.map((item, index)=>{
                return <Body.AlertText key={index.toString()}>● {item}</Body.AlertText>
            })

    }

     const getTextBomb = (value: number) => {
        switch(value){
            case SituacaoBomba.OK:
                return "Bombas em funcionamento"
            case SituacaoBomba.PROBLEMA_BOMBA_1:
                return "Bomba 1 parada"
            case SituacaoBomba.PROBLEMA_BOMBA_2:
                return "Bomba 2 parada"
            case SituacaoBomba.PROBLEMA_BOMBA_3:
                return "Bomba 3 parada"
            case SituacaoBomba.PROBLEMA_BOMBA_1_E_2:
                return "Bombas 1 e 2 paradas"
            case SituacaoBomba.PROBLEMA_BOMBA_1_E_3:
                return "Bombas 1 e 3 paradas"
            case SituacaoBomba.PROBLEMA_BOMBA_2_E_3:
                return "Bombas 2 e 3 paradas"
            case SituacaoBomba.PROBLEMA_TRES_BOMBAS:
                return "Bombas 1, 2 e 3 paradas"
            default:
                return "Erro ao encontrar status"
        }
    }

    const selectUsina = (token: string, index: number) => {
        setLoading(true)
        setTrocandoPara(currentUsina?.moendas[index]?.nome ?? null)
        fetchURL.current = `${getApiBaseUrl()}/dashboard?token=${token}`
        setCurrentUsinaIndex(index)
        setModalState(false)
    }

    // Avança circularmente para a próxima usina e dispara fetch imediato —
    // selectUsina só atualiza fetchURL.current, sem isso a tela ficaria com
    // dados antigos até o próximo ciclo do AutoProgressBar de refresh (20s).
    const handleAutoSwitchTick = () => {
        if (!currentUsina || currentUsina.moendas.length < 2) return
        const nextIndex = (currentUsinaIndex + 1) % currentUsina.moendas.length
        selectUsina(currentUsina.moendas[nextIndex].token, nextIndex)
        handleComplete()
    }

    // ── Severidade das luzes de fundo — derivada do estado já existente ──
    const getManutencaoSeverity = (UH: Types.UnidadeHidraulicaType): 'ok' | 'atencao' | 'critico' => {
        const tempOk = !isSensorInvalido(UH.oleo.temperatura) && UH.oleo.temperatura > 25

        if (!isSensorInvalido(UH.oleo.temperatura) && UH.oleo.temperatura >= TH.temperatura.critico) return 'critico'
        if (!isSensorInvalido(UH.oleo.trans_umidade) && UH.oleo.trans_umidade > TH.umidade.critico) return 'critico'
        if (tempOk && UH.oleo.trans_fil_suj_1 >= TH.filtro.critico) return 'critico'
        if (tempOk && UH.oleo.trans_fil_suj_2 !== null && UH.oleo.trans_fil_suj_2 >= TH.filtro.critico) return 'critico'
        if (UH.bomba.situacao !== SituacaoBomba.OK) return 'critico'

        if ([UH.oleo.temperatura, UH.oleo.trans_umidade, UH.oleo.trans_fil_suj_1, UH.oleo.trans_fil_suj_2]
            .filter((v): v is number => v !== null).some(isSensorInvalido)) return 'atencao'
        if (!isSensorInvalido(UH.oleo.temperatura) && UH.oleo.temperatura >= TH.temperatura.atencao) return 'atencao'
        if (!isSensorInvalido(UH.oleo.trans_umidade) && UH.oleo.trans_umidade > TH.umidade.atencao) return 'atencao'
        if (tempOk && UH.oleo.trans_fil_suj_1 >= TH.filtro.atencao) return 'atencao'
        if (tempOk && UH.oleo.trans_fil_suj_2 !== null && UH.oleo.trans_fil_suj_2 >= TH.filtro.atencao) return 'atencao'
        if (UH.oleo.alarme_conjunto_filtro_1 === 2 || UH.oleo.alarme_conjunto_filtro_2 === 2) return 'atencao'

        return 'ok'
    }

    const getAlertasSeverity = (): 'ok' | 'atencao' | 'critico' => {
        if (!currentUsina) return 'ok'
        if (currentUsina.moendaAtual.offline) return 'atencao'

        const manutencao = getManutencaoSeverity(currentUsina.moendaAtual.unidadeHidraulica)
        if (manutencao === 'critico') return 'critico'

        const algumParado = currentUsina.moendaAtual.ternos.some(t => !t.funcionando)
        if (algumParado) return 'critico'

        const algumManual = currentUsina.moendaAtual.ternos.some(t => t.manual)
        if (manutencao === 'atencao' || algumManual) return 'atencao'

        return 'ok'
    }

    if (isMobile)
        return null

    if(!currentUsina)
        return <Loading/>

    return <Body.Container>
        <Body.BgLights>
            <Body.BlobFixedBlue />
            <Body.BlobAlertas severity={getAlertasSeverity()} />
            <Body.BlobManutencao severity={getManutencaoSeverity(currentUsina.moendaAtual.unidadeHidraulica)} />
        </Body.BgLights>

        <Body.TvScaleWrapper tvMode={tvMode}>

        <Body.HeaderBar>
            <Body.TitleBlock>
                <Body.Eyebrow>Dashboard do DHM4.0</Body.Eyebrow>
                <Body.UsinaTitle>{currentUsina.moendaAtual.nome}</Body.UsinaTitle>
            </Body.TitleBlock>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                {currentUsina.moendas.length > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'Nunito', fontSize: 12, color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap' }}>Troca automática</span>
                        <ToggleSwitch isOn={autoSwitchOn} onToggle={() => setAutoSwitchOn(v => !v)} />
                        {autoSwitchOn && (
                            <>
                                <input type="number" min={AUTO_SWITCH_MIN_SECONDS} value={autoSwitchSeconds}
                                    onChange={e => setAutoSwitchSeconds(Math.max(AUTO_SWITCH_MIN_SECONDS, Number(e.target.value) || AUTO_SWITCH_MIN_SECONDS))}
                                    style={{ width: 50, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontFamily: 'Nunito', fontSize: 12, padding: '4px 6px' }} />
                                <span style={{ fontFamily: 'Nunito', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>s</span>
                            </>
                        )}
                    </div>
                )}
                {/* <button onClick={() => setTvMode(v => !v)} title={tvMode ? 'Modo Desktop' : 'Modo TV (leitura à distância)'}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: tvMode ? 'rgba(82,146,209,0.22)' : 'rgba(255,255,255,0.06)', border: tvMode ? '1px solid rgba(82,146,209,0.4)' : '1px solid rgba(255,255,255,0.1)', borderRadius: 999, height: 40, padding: '0 16px', color: '#fff', fontFamily: 'Nunito', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                    {tvMode ? <Monitor size={16} /> : <Tv size={16} />}
                    {tvMode ? 'Desktop' : 'TV'}
                </button> */}
                <Body.MoendaButton onClick={() => setModalState(prev => !prev)}>Selecionar usina</Body.MoendaButton>
            </div>
        </Body.HeaderBar>

        <Body.HeaderSubGroup>

        <div style={{ flex: '1 1 0%', minWidth: 0 }}>
            <Body.Title>Ternos</Body.Title>
            <Body.TernosArea>
                {currentUsina.grafico.map((item) => (
                    <Body.GraficoArea key={item.terno_id}>
                        <Body.TernoTitle>Terno {item.terno_id}</Body.TernoTitle>
                        <SimpleTernoChart data={item.data} />
                    </Body.GraficoArea>
                ))}
            </Body.TernosArea>
        </div>

        <div style={{ flex: '0 1 340px', minWidth: 0 }}>
            <Body.Title style={{marginLeft: 10}}>Alertas</Body.Title>
            <Body.AlertasArea>
                {getAlertsOil(currentUsina.moendaAtual.unidadeHidraulica)}
            </Body.AlertasArea>
        </div>


        </Body.HeaderSubGroup>

        <Body.Title>Manutenção</Body.Title>
        <Body.ManutencaoArea>

            <Body.GradientContent>
                <Body.MoendaText>Transmissor de pressão 1</Body.MoendaText>
                <Body.MoendaTitle>{currentUsina.moendaAtual.unidadeHidraulica.bomba.transmissor_pressao_1.toFixed(0)} psi</Body.MoendaTitle>
            </Body.GradientContent>

            <Body.GradientContent>
                <Body.MoendaText>Transmissor de pressão 2</Body.MoendaText>
                <Body.MoendaTitle>{currentUsina.moendaAtual.unidadeHidraulica.bomba.transmissor_pressao_2.toFixed(0)} psi</Body.MoendaTitle>
            </Body.GradientContent>

            {currentUsina.moendaAtual.unidadeHidraulica.bomba.transmissor_pressao_3 ?
            <Body.GradientContent>
                <Body.MoendaText>Transmissor de pressão 3</Body.MoendaText>
                <Body.MoendaTitle>{currentUsina.moendaAtual.unidadeHidraulica.bomba.transmissor_pressao_3.toFixed(0)} psi</Body.MoendaTitle>
            </Body.GradientContent> : null}

            <Body.GradientContent>
                <Body.MoendaText>Autoteste bomba 1</Body.MoendaText>
                <Body.GradientAutoteste />
                <Body.RailPickup>
                    <Body.Pickup
                        left={Functions.getAutotestePickup(
                            currentUsina.moendaAtual.unidadeHidraulica.bomba.autoteste_bomba_1)}>
                        <Body.CirclePickup />
                        <Body.TextPickup>
                            {currentUsina.moendaAtual.unidadeHidraulica.bomba.autoteste_bomba_1.toFixed(0)} psi
                        </Body.TextPickup>
                    </Body.Pickup>
                </Body.RailPickup>
            </Body.GradientContent>

            <Body.GradientContent>
                <Body.MoendaText>Autoteste bomba 2</Body.MoendaText>
                <Body.GradientAutoteste />
                <Body.RailPickup>
                    <Body.Pickup
                        left={Functions.getAutotestePickup(
                            currentUsina.moendaAtual.unidadeHidraulica.bomba.autoteste_bomba_2)}>
                        <Body.CirclePickup />
                        <Body.TextPickup>
                            {currentUsina.moendaAtual.unidadeHidraulica.bomba.autoteste_bomba_2.toFixed(0)} psi
                        </Body.TextPickup>
                    </Body.Pickup>
                </Body.RailPickup>
            </Body.GradientContent>

            {currentUsina.moendaAtual.unidadeHidraulica.bomba.autoteste_bomba_3 ?
            <Body.GradientContent>
                <Body.MoendaText>Autoteste bomba 3</Body.MoendaText>
                <Body.GradientAutoteste />
                <Body.RailPickup>
                    <Body.Pickup
                        left={Functions.getAutotestePickup(
                            currentUsina.moendaAtual.unidadeHidraulica.bomba.autoteste_bomba_3)}>
                        <Body.CirclePickup />
                        <Body.TextPickup>
                            {currentUsina.moendaAtual.unidadeHidraulica.bomba.autoteste_bomba_3.toFixed(0)} psi
                        </Body.TextPickup>
                    </Body.Pickup>
                </Body.RailPickup>
            </Body.GradientContent> : null}

            <Body.GradientContent>
                <Body.MoendaText>
                    Status das bombas
                </Body.MoendaText>
                <Body.Row >
                    <Body.MoendaTitle>
                        {getTextBomb(currentUsina.moendaAtual.unidadeHidraulica.bomba.situacao)}
                    </Body.MoendaTitle>
                </Body.Row>
            </Body.GradientContent>

            <Body.GradientContent>
                <Body.MoendaText>Temperatura do óleo</Body.MoendaText>
                {isSensorInvalido(currentUsina.moendaAtual.unidadeHidraulica.oleo.temperatura)
                    ? <SensorBadge label={getSensorLabel(currentUsina.moendaAtual.unidadeHidraulica.oleo.temperatura)!} />
                    : <><Body.GradientUmidade />
                      <Body.RailPickup>
                        <Body.Pickup left={Functions.getTemperaturePickup(currentUsina.moendaAtual.unidadeHidraulica.oleo.temperatura)}>
                            <Body.CirclePickup />
                            <Body.TextPickup>
                                {currentUsina.moendaAtual.unidadeHidraulica.oleo.temperatura.toFixed(1)} ºC
                            </Body.TextPickup>
                        </Body.Pickup>
                      </Body.RailPickup></>
                }
            </Body.GradientContent>

            <Body.GradientContent>
                <Body.MoendaText>Transmissor de umidade</Body.MoendaText>
                {isSensorInvalido(currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_umidade)
                    ? <SensorBadge label={getSensorLabel(currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_umidade)!} />
                    : <><Body.GradientUmidade />
                      <Body.RailPickup>
                        <Body.Pickup left={currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_umidade}>
                            <Body.CirclePickup />
                            <Body.TextPickup>
                                {currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_umidade.toFixed(1)} %
                            </Body.TextPickup>
                        </Body.Pickup>
                      </Body.RailPickup></>
                }
            </Body.GradientContent>

            <Body.GradientContent>
                <Body.MoendaText style={{marginBottom: 10}}>Transmissor de filtro sujo 1</Body.MoendaText>
                {isSensorInvalido(currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_fil_suj_1)
                    ? <SensorBadge label={getSensorLabel(currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_fil_suj_1)!} />
                    : <><Body.GradientUmidade />
                      <Body.RailPickup>
                        <Body.Pickup left={Functions.getFiltroPickup(currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_fil_suj_1)}>
                            <Body.CirclePickup />
                            <Body.TextPickup>
                                {currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_fil_suj_1.toFixed(1)} bar
                            </Body.TextPickup>
                        </Body.Pickup>
                      </Body.RailPickup></>
                }
            </Body.GradientContent>

            {currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_fil_suj_2 !== null ?
            <Body.GradientContent>
                <Body.MoendaText style={{marginBottom: 10}}>Transmissor de filtro sujo 2</Body.MoendaText>
                {isSensorInvalido(currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_fil_suj_2)
                    ? <SensorBadge label={getSensorLabel(currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_fil_suj_2)!} />
                    : <><Body.GradientUmidade />
                      <Body.RailPickup>
                        <Body.Pickup left={Functions.getFiltroPickup(currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_fil_suj_2)}>
                            <Body.CirclePickup />
                            <Body.TextPickup>
                                {currentUsina.moendaAtual.unidadeHidraulica.oleo.trans_fil_suj_2.toFixed(1)} bar
                            </Body.TextPickup>
                        </Body.Pickup>
                      </Body.RailPickup></>
                }
            </Body.GradientContent> : null}

        </Body.ManutencaoArea>

        <Body.FooterText style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            {currentUsina?.moendaAtual.offline
                ? <><AlertTriangle size={13} color="#E5D34A" strokeWidth={2} style={{ flexShrink: 0 }} />{"Moenda está sem enviar dados desde " + geUTCStringFromTimestamp(currentUsina?.moendaAtual.ultimo_envio) + ". Exibindo últimos valores salvos."}</>
                : "Última atualização: " + geUTCStringFromTimestamp(currentUsina?.moendaAtual.ultimo_envio)
            }
        </Body.FooterText>

        </Body.TvScaleWrapper>

        {isModalOpen && (
            <Modal title="Selecione uma usina" toggleModal={setModalState}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 320 }}>
                    {currentUsina.moendas.map((usina, index) => (
                        <Button key={usina.nome} text={usina.nome} action={() => selectUsina(usina.token, index)} />
                    ))}
                </div>
            </Modal>
        )}

        <AutoProgressBar onComplete={handleComplete}/>
        {autoSwitchOn && currentUsina.moendas.length > 1 && (
            <AutoProgressBar durationMs={autoSwitchSeconds * 1000} onComplete={handleAutoSwitchTick} />
        )}

        {/* Já existe uma usina em tela (passamos pelo `if(!currentUsina) return <Loading/>` acima) —
            não bloquear a visualização numa troca manual/automática, só indicar discretamente. */}
        {isLoading && (
            <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 600, background: 'rgba(23,33,46,0.95)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.4)' }}>
                <Body.SpinnerIcon src={Spinner} alt="" />
                <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#fff' }}>
                    {trocandoPara ? `Trocando para ${trocandoPara}...` : 'Atualizando...'}
                </span>
            </div>
        )}

    </Body.Container>
}

export default Dashboard
